/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Matěj Grabovský <matej.grabovsky@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

/**
 * Navigátor mezi prvky na které lze zaměřit focus.
 * Dokumentace:
 * [HTML 5.2 Focus Specification](https://www.w3.org/TR/html52/editing.html#focus),
 * [Document.activeElement](https://developer.mozilla.org/en-US/docs/Web/API/DocumentOrShadowRoot/activeElement).
 */
class FocusNavigator {
  /**
   * Vytvoří instanci.
   * @param {Document} document
   * @returns {FocusNavigator} Nová instance.
   */
  constructor(document) {
    this.document = document;
  }

  /**
   * Přenese focus na element s libovolným offsetem.
   * @param {number} offset Počet elementů o kolik se má přejít.
   * @private
   * @returns {?HTMLElement} Element na který byl přenesený focus, nebo `null`.
   */
  change(offset) {
    const elArray = this.queryElements();
    const el = this.findElement(elArray, offset, {
      current: this.document.activeElement
    });
    if (!isNil(el)) {
      el.focus();
    }
    return el;
  }

  /**
   * Najde element v poli s libovolným offsetem od aktuálního.
   * @param {Array.<HTMLElement>} array Pole s elementy.
   * @param {number} offset Počet elementů o kolik se má přejít.
   * @param {Object} [opts={}] Nepovinné parametry.
   * @param {HTMLElement} [opts.current] Aktuální element v poli, od kterého se počítá.
   * @private
   * @returns {?HTMLElement} Element na který byl přenesený focus, nebo `null`.
   */
  findElement(array, offset, { current } = {}) {
    const fromIndex = this.findFromIndex(array, { current });
    const toIndex = this.findToIndex(fromIndex, array.length, offset);
    if (toIndex === -1) {
      return null;
    }
    return array[toIndex];
  }

  /**
   * Najde index aktuálního elementu v poli.
   * @param {Array.<HTMLElement>} array Pole s elementy.
   * @param {Object} [opts={}] Nepovinné parametry.
   * @param {HTMLElement} [opts.current] Aktuální element v poli, jehož index se hledá.
   * @private
   * @returns {number}
   */
  findFromIndex(array, { current } = {}) {
    const index = array.indexOf(current);
    if (index === -1) {
      // Pokud aktuálně focusovaný prvek není v seznamu akceptovaných,
      // přenese se focus na první takový prvek.
      return 0;
    }
    return index;
  }

  /**
   * Najde index elementu s offsetem.
   * @param {number} current Index aktuálního elementu.
   * @param {number} length Celkový počet elementů.
   * @param {number} offset Počet elementů o kolik se má přejít.
   * @private
   * @returns {number} Nalezený index, nebo `-1`.
   */
  findToIndex(current, length, offset) {
    if (length === 0) {
      // Pokud nejsou dostupné žádné elementy, nelze nic dělat.
      return -1;
    }
    if (offset < 0) {
      // Najde index při záporném offsetu.
      return (current + length + offset) % length;
    }
    // Najde index při nulovém, nebo kladném offsetu.
    return (current + offset) % length;
  }

  /**
   * Přenese focus na další element.
   * Emuluje chování jako při stisku klávesy `Tab`.
   * @returns {?HTMLElement} Element na který byl přenesený focus, nebo `null`.
   */
  next() {
    return this.change(1);
  }

  /**
   * Přenese focus na předcházející element.
   * Emuluje chování jako při stisku kláves `Shift + Tab`.
   * @returns {?HTMLElement} Element na který byl přenesený focus, nebo `null`.
   */
  previous() {
    return this.change(-1);
  }

  /**
   * Vyhledá seznam prvků, na které lze přenést focus.
   * Velice zjednodušená implementace, která ignoruje řadu požadavků ze specifikace, mimo jiné:
   * `<area>`, `<dialog>`, `<object>`, `<fieldset disabled>`, `tabindex`, `display`, `visibility`, `Selection`, `click`.
   * Propracovanější implementaci lze nalézt třeba
   * u [jQuery UI](https://github.com/jquery/jquery-ui/blob/0b7246b6eeadfa9e2696e22f3230f6452f8129dc/ui/focusable.js).
   * @private
   * @returns {Array.<Element>}
   */
  queryElements() {
    return Array.from(
      this.document.querySelectorAll('a, button, input, select, textarea')
    ).filter((el) => {
      if (el.tagName.toLowerCase() === 'a') {
        // Odkaz musí mít definovaný href.
        return el.href;
      }
      // Formulářové prvky nesmějí být disabled.
      return !el.disabled;
    });
  }
}

export default FocusNavigator;
